/**
 * Wall clocks, in a zone you name — cities plan C2.1 (D3).
 *
 * `Europe/Berlin` was hard-coded in 21 files, each with its own module-level
 * `Intl.DateTimeFormat`. One zone did two jobs (survey trap T3): what "today"
 * means for the person looking, and what time a showing starts where it
 * happens. They agree while every row is a Berlin row and stop agreeing the
 * day a Boston row arrives, so they are two questions here:
 *
 *   - `hereZone()` — the current region's zone. "Today", "tonight", a task's
 *     due day, how long ago something was checked.
 *   - `zoneOfCity(row.city)` — the zone of the region a row's town belongs
 *     to. A showing's day and clock, the midnight-means-untimed test.
 *
 * Both are set by `regions.ts` from the regions it knows (bundled at load,
 * then the live tables). This file holds them rather than `regions.ts` so
 * the files the Worker's suite loads directly — `today.ts`, `rowMeta.ts`,
 * `ics.ts`, `calendar.ts` — can ask without importing React or Supabase.
 * Until anything is set, both answer `FALLBACK_ZONE`.
 *
 * Every function below takes the zone last and has no default: a caller
 * that forgets which zone it means is a type error, not Berlin.
 *
 * Import-free on purpose: `node --test` loads it straight from the root
 * suite, and so does every file that imports it with its extension.
 */

/**
 * The zone of a row whose region nobody can name — survey trap T2: a row
 * with no city, or a town in no region, is a Berlin row until the harvest
 * writes a city on every row (C1.x). Also what `hereZone()` answers before
 * `regions.ts` has said anything. Operator-level days that stay on Berlin on
 * purpose (D3) read this too, by name, so a search for it finds them.
 */
export const FALLBACK_ZONE = 'Europe/Berlin'

// ---------------------------------------------------------------------------
// The two zones the app reads in
// ---------------------------------------------------------------------------

export interface Zones {
  /** The current region's zone: what "today" means. */
  here: string
  /** A row's zone, from its town. Never throws; an unknown town is T2's. */
  of: (city: string | null | undefined) => string
}

let zones: Zones = { here: FALLBACK_ZONE, of: () => FALLBACK_ZONE }

/** Installed by `regions.ts`. Idempotent; a caller that renders before it is
 *  called reads `FALLBACK_ZONE` for both, which is what it read before C2.1. */
export function setZones(next: Zones): void {
  zones = next
}

/** The current region's zone — the zone "today" is read in. */
export function hereZone(): string {
  return zones.here
}

/** The zone a row's times are read in: its town's region's, or T2's. */
export function zoneOfCity(city: string | null | undefined): string {
  return zones.of(city)
}

// ---------------------------------------------------------------------------
// Formatters, one per zone and format, built once
// ---------------------------------------------------------------------------

// `toLocale*String` with an options bag builds a fresh `Intl.DateTimeFormat`
// on every call, and that construction dominated a CPU profile of the
// deployed app (`today.ts`'s note, a4-13). One formatter per zone per format,
// plus a bounded memo per zone keyed by the input — the string as given, or a
// Date's epoch ms — because a feed asks about the same few thousand
// timestamps on every render.

const CACHE_CAP = 20_000

interface Memo<T> { fmt: Intl.DateTimeFormat; cache: Map<string | number, T> }

const byZone = <T>(make: (tz: string) => Intl.DateTimeFormat) => {
  const zones = new Map<string, Memo<T>>()
  return (tz: string): Memo<T> => {
    let m = zones.get(tz)
    if (!m) {
      m = { fmt: make(tz), cache: new Map() }
      zones.set(tz, m)
    }
    return m
  }
}

/** Memoise `fn` over a moment within one zone. An invalid moment is never
 *  cached and answers `invalid` — the string `toLocale*String` gave it — rather
 *  than the `RangeError` `Intl.DateTimeFormat#format` throws. */
function memoised<T>(m: Memo<T>, at: string | Date | number, invalid: T, fn: (d: Date) => T): T {
  const key = typeof at === 'object' ? at.getTime() : at
  const hit = m.cache.get(key)
  if (hit !== undefined) return hit
  const d = new Date(at)
  if (Number.isNaN(d.getTime())) return invalid
  const out = fn(d)
  if (m.cache.size >= CACHE_CAP) m.cache.clear()
  m.cache.set(key, out)
  return out
}

const dayFmts = byZone<string>(tz => new Intl.DateTimeFormat('en-CA', {
  timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric',
}))
// `hourCycle: 'h23'` rather than `hour12: false`, which some engines render
// as "24:00" at midnight; every caller that tests for midnight still accepts
// both.
const clockFmts = byZone<string>(tz => new Intl.DateTimeFormat('en-GB', {
  timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
}))
const wallFmts = byZone<number>(tz => new Intl.DateTimeFormat('sv-SE', {
  timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric',
  hour: 'numeric', minute: 'numeric', second: 'numeric',
}))

/** The calendar day `instant` falls on in `tz`, `YYYY-MM-DD`. Not
 *  `slice(0, 10)`, which is the UTC day: a 00:30 Berlin screening is stored
 *  as 22:30 the evening before in summer. `'Invalid Date'` for a moment that
 *  does not parse, as `toLocaleDateString` said. */
export function dayIn(instant: string | Date, tz: string): string {
  const m = dayFmts(tz)
  return memoised(m, instant, 'Invalid Date', d => m.fmt.format(d))
}

/** The wall clock `instant` reads in `tz`, `HH:MM`, 24-hour (D4: one clock
 *  everywhere). `'Invalid Date'` for a moment that does not parse. */
export function clockIn(instant: string | Date, tz: string): string {
  const m = clockFmts(tz)
  return memoised(m, instant, 'Invalid Date', d => m.fmt.format(d))
}

/** How many minutes `tz` is ahead of UTC at `instant`. Formatting the instant
 *  in the zone and reading the result back as though it were UTC leaves
 *  exactly the offset — `sv-SE` because its date format is already ISO. */
export function offsetMinIn(instant: string | Date, tz: string): number {
  const m = wallFmts(tz)
  return memoised(m, instant, NaN, d =>
    (Date.parse(`${m.fmt.format(d).replace(' ', 'T')}Z`) - d.getTime()) / 60000)
}

/**
 * The instant at which a wall clock in `tz` reads `clock` (`HH:MM`) on `day`.
 *
 * Two passes, because the offset to subtract is the offset *at the answer*,
 * not at the guess: reading it off the naive instant and then again off the
 * corrected one settles every minute of the year except those that do not
 * exist on the spring-forward morning, where the second pass lands an hour
 * later — the right answer for a window, and the only one there is.
 */
export function instantIn(day: string, clock: string, tz: string): Date {
  const naive = Date.parse(`${day}T${clock}:00Z`)
  const first = new Date(naive - offsetMinIn(new Date(naive), tz) * 60000)
  return new Date(naive - offsetMinIn(first, tz) * 60000)
}

/**
 * Midnight on `day` in `tz` as an ISO string carrying that day's real offset
 * (`2026-12-14T00:00:00+01:00`), for a bare day with no showing to carry a
 * time — the stored placeholder for "a date, no time". A fixed offset gets
 * the day wrong across a clock change (`ics.ts`'s note on the Berlin copy
 * this replaces). The offset is the one in force at that midnight: the Berlin
 * copy read it at noon, which on the two clock-change Sundays is the other
 * offset, so `2026-03-29` came back as 23:00 on the 28th.
 */
export function midnightIsoIn(day: string, tz: string): string {
  const min = offsetMinIn(instantIn(day, '00:00', tz), tz)
  const sign = min < 0 ? '-' : '+'
  const abs = Math.abs(min)
  const hh = String(Math.floor(abs / 60)).padStart(2, '0')
  const mm = String(abs % 60).padStart(2, '0')
  return `${day}T00:00:00${sign}${hh}:${mm}`
}
